import _ from 'lodash';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, createTheme, ThemeProvider, useTheme, Tooltip, Typography, Stack, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import domtoimage from 'dom-to-image';
import jsPDF from 'jspdf';
import { MaterialReactTable } from 'material-react-table';
import AllInputs from '../../../../inputs/AllInputs';
import {
  addVehicleProperty,
  addVehiclePropertyVehicleRefresh,
  addVehiclePropertyRaw,
  updateVehicle,
  updateVehicleData,
  updateVehicleDepartment,
  deleteVehicleProperty,
} from '../../../../actions/AllUpdates';
import { getCurrentDepartment } from '../../../../actions/apiGetInfo';
import { vsearch, activeVehicle } from '../../../../actions/lookups';

const InspectionBuildForm = () => {
  const dispatch = useDispatch();
  const current_dealership = useSelector((state) => state.settings_dealerships.current_dealership);
  const installation = useSelector((state) => state.settings_dealerships.installation);
  const active_vehicle = useSelector((state) => state.settings_refresh.active_vehicle);
  const current_tab = useSelector((state) => state.settings_nav.menus.vehicle_tab);
  const current_user = useSelector((state) => state.current_user);
  const vehicle_filter = useSelector((state) => state.settings_nav.vehicle_filter);
  const department = useSelector((state) => state.settings_nav.department);
  const dealer_settings = useSelector((state) => state.settings_site.dealer_settings);
  const default_labor_rate = _.find(dealer_settings, o => o.key === 'default_labor_rate' && o.dealership === installation.subdomain);
  const default_labor_rate_value = default_labor_rate ? default_labor_rate.value : 100;
  const [customRows, setCustomRows] = useState([]);
  const [rowTotals, setRowTotals] = useState({});
  const [grandTotal, setGrandTotal] = useState('0.00');
  const [completedTotal, setCompletedTotal] = useState('0.00');
  const [approvedTotal, setApprovedTotal] = useState('0.00');
  const [pendingTotal, setPendingTotal] = useState('0.00');
  const [error, setError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteRowName, setDeleteRowName] = useState(null);
  const [deleteRowId, setDeleteRowId] = useState(null);
  const [deleteRowKey, setDeleteRowKey] = useState(null);
  const tableRef = useRef();
  const initializedRowsRef = useRef(new Set());

  const vehiclePropertiesMap = useMemo(() => {
    const map = {};
    if (active_vehicle && active_vehicle.vehicle_properties) {
      active_vehicle.vehicle_properties.forEach((prop) => {
        map[prop.key] = prop;
      });
    }
    return map;
  }, [active_vehicle]);

  const vehicleProperty = (name) => {
    return vehiclePropertiesMap[name] || { value: '', user_name: '', updated_at: '', id: null };
  };

  useEffect(() => {
    if (active_vehicle && active_vehicle.vehicle_properties) {
      const existingCustomRows = active_vehicle.vehicle_properties
        .filter((prop) => prop.key.match(/^custom_inspection[0-9]+$/))
        .map((prop) => prop.key)
        .sort((a, b) => {
          const numA = parseInt(a.match(/[0-9]+$/)[0]);
          const numB = parseInt(b.match(/[0-9]+$/)[0]);
          return numA - numB;
        });
      setCustomRows(existingCustomRows.length > 0 ? existingCustomRows : []);
    }
  }, [active_vehicle?.vehicle_properties]);

  useEffect(() => {
    if (active_vehicle && active_vehicle.vehicle_properties) {
      const defaultRowProps = [

        {
          row: 'Tire Tread Depth',
          props: [{ key: '_Labor', value: default_labor_rate_value.toString() }],
        },
        {
          row: 'Brake Pads/Shoes',
          props: [{ key: '_Labor', value: default_labor_rate_value.toString() }],
        },
        {
          row: 'Brake Rotor Specs',
          props: [{ key: '_Labor', value: default_labor_rate_value.toString() }],
        },
        {
          row: 'State Inspection',
          props: [
            { key: '_Time', value: '0.222' },
            { key: '_Quantity', value: '1' },
            { key: '_Parts', value: '14.00' },
          ],
        },
        {
          row: 'Emissions',
          props: [
            { key: '_Time', value: '0.24968' },
            { key: '_Quantity', value: '1' },
            { key: '_Parts', value: '1.57' },
          ],
        },
        {
          row: 'Pre-Owned Inspection',
          props: [
            { key: '_Time', value: '1.2' },
            { key: '_Quantity', value: '0' },
            { key: '_Parts', value: '0.00' },
          ],
        },
      ];

      const propsToSet = defaultRowProps
        .filter(({ row }) => !initializedRowsRef.current.has(row))
        .flatMap(({ row, props }) =>
          props
            .filter(({ key }) => !vehicleProperty(`${row}${key}`).value || vehicleProperty(`${row}${key}`).value === '0')
            .map(({ key, value }) => ({
              vehicle_id: active_vehicle.id,
              key: `${row}${key}`,
              value,
              uid: current_user.user,
              user_name: current_user.user_name || current_user.email,
            }))
        );

      if (propsToSet.length > 0) {
        Promise.all(
          propsToSet.map((prop) =>
            dispatch(addVehiclePropertyRaw(prop, 'updateVehicleError'))
          )
        )
          .then(() => {
            defaultRowProps.forEach(({ row }) => initializedRowsRef.current.add(row));
            return dispatch(activeVehicle(active_vehicle.id));
          })
          .then(() => {
            updateTotals();
          })
          .catch((err) => {
            setError('Failed to initialize default values. Please try again.');
          });
      }
    }
  }, [active_vehicle?.vehicle_properties]);

  useEffect(() => {
    if (data.length > 0) {
      updateTotals();
    }
  }, [data, vehiclePropertiesMap]);

  const addCustomRow = () => {
    const existingIndices = (active_vehicle?.vehicle_properties || [])
      .filter((prop) => prop.key.match(/^custom_inspection[0-9]+$/))
      .map((prop) => parseInt(prop.key.match(/[0-9]+$/)[0]));
    const maxIndex = existingIndices.length > 0 ? Math.max(...existingIndices) : 0;
    const newIndex = maxIndex + 1;
    const newRowName = `custom_inspection${newIndex}`;
    const prevCustomRows = [...customRows];

    setCustomRows([...customRows, newRowName]);

    const data = {
      vehicle_id: active_vehicle.id,
      key: newRowName,
      value: '',
      uid: current_user.user,
      user_name: current_user.user_name || current_user.email,
    };
    dispatch(addVehiclePropertyVehicleRefresh(data, 'updateVehicleError'))
      .then(() => {
        const initialProps = [
          { key: `${newRowName}_Pass`, value: '' },
          { key: `${newRowName}_Recommend`, value: '' },
          { key: `${newRowName}_Required`, value: '' },
          { key: `${newRowName}_Approved`, value: '' },
          { key: `${newRowName}_Time`, value: '' },
          { key: `${newRowName}_Labor`, value: default_labor_rate_value.toString() },
          { key: `${newRowName}_LaborTotal`, value: '0.00' },
          { key: `${newRowName}_Quantity`, value: '' },
          { key: `${newRowName}_Parts`, value: '' },
          { key: `${newRowName}_Completed`, value: '' },
        ];
        return Promise.all(
          initialProps.map((prop) =>
            dispatch(
              addVehiclePropertyRaw(
                {
                  vehicle_id: active_vehicle.id,
                  key: prop.key,
                  value: prop.value,
                  uid: current_user.user,
                  user_name: current_user.user_name || current_user.email,
                },
                'updateVehicleError'
              )
            )
          )
        );
      })
      .then(() => {
        console.log('addCustomRow: All properties saved, refreshing with activeVehicle');
        return dispatch(activeVehicle(active_vehicle.id));
      })
      .then(() => {
        updateTotals();
      })
      .catch((err) => {
        setError(`Failed to add custom row: ${err.message || 'Unknown error'}. Please try again.`);
        setCustomRows(prevCustomRows);
      });
  };

  const handleOpenDeleteModal = (rowName, rowId) => {
    const rowValue = vehicleProperty(rowName).value || '';
    setDeleteRowName(rowValue);
    setDeleteRowKey(rowName);
    setDeleteRowId(rowId);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteRowName(null);
    setDeleteRowKey(null);
    setDeleteRowId(null);
  };

  const handleConfirmDelete = () => {
    if (deleteRowId && deleteRowKey && deleteRowKey.match(/^custom_inspection[0-9]+$/)) {
      dispatch(deleteVehicleProperty(deleteRowId, active_vehicle.id, 'deleteVehiclePropertyError'))
        .then(() => {
          setCustomRows(customRows.filter((r) => r !== deleteRowKey));
          const relatedProps = [
            `${deleteRowKey}_Pass`,
            `${deleteRowKey}_Recommend`,
            `${deleteRowKey}_Required`,
            `${deleteRowKey}_Approved`,
            `${deleteRowKey}_Time`,
            `${deleteRowKey}_Labor`,
            `${deleteRowKey}_LaborTotal`,
            `${deleteRowKey}_Quantity`,
            `${deleteRowKey}_Parts`,
            `${deleteRowKey}_Completed`,
          ];
          return Promise.all(
            relatedProps.map((key) => {
              const relatedProp = vehiclePropertiesMap[key] || { id: null };
              if (relatedProp.id) {
                return dispatch(
                  deleteVehicleProperty(relatedProp.id, active_vehicle.id, 'deleteVehiclePropertyError')
                );
              }
              return Promise.resolve();
            })
          );
        })
        .then(() => {
          console.log('deleteCustomRow: Refreshing with activeVehicle');
          return dispatch(activeVehicle(active_vehicle.id));
        })
        .then(() => {
          updateTotals();
        })
        .catch((err) => {
          setError(`Failed to delete row: ${err.message || 'Unknown error'}. Please try again.`);
        });
    } else {
      setError('Cannot delete row: Invalid ID or not a custom row.');
    }
    handleCloseDeleteModal();
  };

  const updateTotals = () => {
    const newTotals = {};
    let completedSum = 0;
    let approvedSum = 0;
    let pendingSum = 0;
    firstRows.forEach((row) => {
      const timeVal = parseFloat(vehicleProperty(`${row}_Time`).value || '0');
      const laborVal = parseFloat(vehicleProperty(`${row}_Labor`).value || default_labor_rate_value);
      const quantityVal = parseFloat(vehicleProperty(`${row}_Quantity`).value || '0');
      const partsVal = parseFloat(vehicleProperty(`${row}_Parts`).value || '0');
      const laborTotal = timeVal * laborVal;
      const total = laborTotal + (quantityVal * partsVal);
      newTotals[row] = {
        laborTotal: isNaN(laborTotal) ? '0.00' : laborTotal.toFixed(2),
        total: isNaN(total) ? '0.00' : total.toFixed(2),
      };
      const isCompleted = vehicleProperty(`${row}_Completed`).value === 'true';
      const isApproved = vehicleProperty(`${row}_Approved`).value === 'true';
      const isRecommended = vehicleProperty(`${row}_Recommend`).value === 'true';
      const isRequired = vehicleProperty(`${row}_Required`).value === 'true';
      if (isCompleted) {
        completedSum += parseFloat(newTotals[row].total);
      }
      if (isApproved) {
        approvedSum += parseFloat(newTotals[row].total);
      }
      if (!(isCompleted || isApproved) && (isRecommended || isRequired || isApproved)) {
        pendingSum += parseFloat(newTotals[row].total);
      }
    });
    const newGrandTotal = Object.values(newTotals)
      .filter(({ total }, index) => {
        const row = firstRows[index];
        return (
          vehicleProperty(`${row}_Recommend`).value === 'true' ||
          vehicleProperty(`${row}_Required`).value === 'true' ||
          vehicleProperty(`${row}_Approved`).value === 'true'
        );
      })
      .reduce((sum, { total }) => sum + parseFloat(total || '0'), 0)
      .toFixed(2);
    setRowTotals(newTotals);
    setGrandTotal(newGrandTotal);
    setCompletedTotal(completedSum.toFixed(2));
    setApprovedTotal(approvedSum.toFixed(2));
    setPendingTotal(pendingSum.toFixed(2));
  };

  const handleCellEdit = (row, column, value) => {
    const field = column.id;
    const rowName = row.original.row_name;
    let processedValue = value;
    if (['time', 'labor', 'quantity', 'parts'].includes(field)) {
      processedValue = value.replace(/[^0-9.]/g, '');
    } else if (['pass', 'recommend', 'required', 'approved', 'completed'].includes(field)) {
      processedValue = value ? 'true' : '';
    }
    const data = {
      vehicle_id: active_vehicle.id,
      key: `${rowName}_${field.charAt(0).toUpperCase() + field.slice(1)}`,
      value: processedValue,
      uid: current_user.user,
      user_name: current_user.user_name || current_user.email,
    };
    dispatch(addVehiclePropertyRaw(data, 'updateVehicleError'))
      .then(() => {
        console.log('handleCellEdit: Refreshing with activeVehicle');
        return dispatch(activeVehicle(active_vehicle.id));
      })
      .then(() => {
        updateTotals();
      })
      .catch((err) => {
        console.error('handleCellEdit: Error:', err.message || err);
        setError(`Failed to save value: ${err.message || 'Unknown error'}. Please try again.`);
      });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  };

  const renderCustomInput = (row, setdisabled) => {
    if (!row.includes('custom')) return null;
    const prop = vehicleProperty(row);
    return (
      <AllInputs
        key={row}
        value={prop.value || ''}
        type="text"
        name={row}
        parentid={active_vehicle.id}
        updatetype="vehicleproperty"
        setdisabled={setdisabled}
        user_name={prop.user_name || ''}
        updated_at={prop.updated_at || ''}
        vehicle={active_vehicle}
        current_user={current_user}
        onChange={() => {
          dispatch(activeVehicle(active_vehicle.id));
          updateTotals();
        }}
      />
    );
  };

  const renderTreadOrPadInput = (row, setdisabled) => {
    if (!['Tread Depth Front', 'Tread Depth Rear', 'Pad Life Front', 'Pad Life Rear', 'Tire Tread Depth', 'Brake Pads/Shoes', 'Brake Rotor Specs'].includes(row)) return null;

    const sides = {
      'Tread Depth Front': ['Tread Depth Front_left', 'Tread Depth Front_right'],
      'Tread Depth Rear': ['Tread Depth Rear_left', 'Tread Depth Rear_right'],
      'Pad Life Front': ['Pad Life Front_left', 'Pad Life Front_right'],
      'Pad Life Rear': ['Pad Life Rear_left', 'Pad Life Rear_right'],
      'Tire Tread Depth': ['Tire Tread Depth_left_front', 'Tire Tread Depth_right_front', 'Tire Tread Depth_left_rear', 'Tire Tread Depth_right_rear'],
      'Brake Pads/Shoes': ['Brake Pads/Shoes_left_front', 'Brake Pads/Shoes_right_front', 'Brake Pads/Shoes_left_rear', 'Brake Pads/Shoes_right_rear'],
      'Brake Rotor Specs': ['Brake Rotor Specs_front_actual', 'Brake Rotor Specs_front_min', 'Brake Rotor Specs_rear_actual', 'Brake Rotor Specs_rear_min'],
    };

    const measurements = sides[row].map((name) => vehicleProperty(name));
    const isFourInputs = measurements.length === 4;

    const labels = row === 'Brake Rotor Specs'
      ? ['Front Actual', 'Front Min', 'Rear Actual', 'Rear Min']
      : isFourInputs
        ? ['L/F', 'R/F', 'L/R', 'R/R']
        : ['Left', 'Right'];

    return (
      <div key={row} className="row">
        <div className="col-md-12">{row}</div>
        <div className={isFourInputs ? 'col-md-6' : 'col-md-6'}>
          <label>{labels[0]}</label>
          <AllInputs
            value={measurements[0].value || ''}
            type="text"
            name={sides[row][0]}
            parentid={active_vehicle.id}
            updatetype="vehicleproperty"
            setdisabled={setdisabled}
            user_name={measurements[0].user_name || ''}
            updated_at={measurements[0].updated_at || ''}
            vehicle={active_vehicle}
            current_user={current_user}
          />
        </div>
        <div className={isFourInputs ? 'col-md-6' : 'col-md-6'}>
          <label>{labels[1]}</label>
          <AllInputs
            value={measurements[1].value || ''}
            type="text"
            name={sides[row][1]}
            parentid={active_vehicle.id}
            updatetype="vehicleproperty"
            setdisabled={setdisabled}
            user_name={measurements[1].user_name || ''}
            updated_at={measurements[1].updated_at || ''}
            vehicle={active_vehicle}
            current_user={current_user}
          />
        </div>
        {isFourInputs && (
          <>
            <div className="col-md-6">
              <label>{labels[2]}</label>
              <AllInputs
                value={measurements[2].value || ''}
                type="text"
                name={sides[row][2]}
                parentid={active_vehicle.id}
                updatetype="vehicleproperty"
                setdisabled={setdisabled}
                user_name={measurements[2].user_name || ''}
                updated_at={measurements[2].updated_at || ''}
                vehicle={active_vehicle}
                current_user={current_user}
              />
            </div>
            <div className="col-md-6">
              <label>{labels[3]}</label>
              <AllInputs
                value={measurements[3].value || ''}
                type="text"
                name={sides[row][3]}
                parentid={active_vehicle.id}
                updatetype="vehicleproperty"
                setdisabled={setdisabled}
                user_name={measurements[3].user_name || ''}
                updated_at={measurements[3].updated_at || ''}
                vehicle={active_vehicle}
                current_user={current_user}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const { firstRows } = useMemo(() => {
    if (current_tab === 'tab_inspection') {
      return {
        firstRows: [
          'Tire Tread Depth',
          'Brake Pads/Shoes',
          'Brake Rotor Specs',
          'State Inspection',
          'Emissions',
          'Pre-Owned Inspection',
          ...customRows,
        ],
      };
    }
    return { firstRows: [] };
  }, [customRows]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'row_name',
        header: '',
        size: 200,
        enableEditing: false,
        Cell: ({ row }) => {
          const rowName = row.original.row_name;
          const setdisabled = false;
          return renderCustomInput(rowName, setdisabled) || renderTreadOrPadInput(rowName, setdisabled) || rowName;
        },
      },
      {
        accessorKey: 'pass',
        header: 'Pass',
        size: 60,
        enableEditing: true,
        muiTableBodyCellProps: ({ cell }) => ({
          sx: {
            backgroundColor: cell.getValue() === 'true' ? '#d4edda' : 'transparent',
            textAlign: 'center',
            paddingTop: '0.5rem',
          },
        }),
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'checkbox',
          onChange: (event) => handleCellEdit(row, cell.column, event.target.checked),
        }),
        Cell: ({ cell, row }) => {
          const prop = vehicleProperty(`${row.original.row_name}_Pass`);
          return (
            <Tooltip
              title={
                cell.getValue() === 'true' && prop.user_name && prop.updated_at
                  ? `${prop.user_name} at ${formatTimestamp(prop.updated_at)}`
                  : ''
              }
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={cell.getValue() === 'true'}
                  onChange={(e) => handleCellEdit(row, cell.column, e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                {cell.getValue() === 'true' && prop.user_name && prop.updated_at && (
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#555' }}>
                    {prop.user_name} <br /> {formatTimestamp(prop.updated_at)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'recommend',
        header: 'Recommend',
        size: 60,
        enableEditing: true,
        muiTableBodyCellProps: ({ cell }) => ({
          sx: {
            backgroundColor: cell.getValue() === 'true' ? '#fff3cd' : 'transparent',
            textAlign: 'center',
            paddingTop: '0.5rem',
          },
        }),
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'checkbox',
          onChange: (event) => handleCellEdit(row, cell.column, event.target.checked),
        }),
        Cell: ({ cell, row }) => {
          const prop = vehicleProperty(`${row.original.row_name}_Recommend`);
          return (
            <Tooltip
              title={
                cell.getValue() === 'true' && prop.user_name && prop.updated_at
                  ? `${prop.user_name} at ${formatTimestamp(prop.updated_at)}`
                  : ''
              }
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={cell.getValue() === 'true'}
                  onChange={(e) => handleCellEdit(row, cell.column, e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                {cell.getValue() === 'true' && prop.user_name && prop.updated_at && (
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#555' }}>
                    {prop.user_name} <br /> {formatTimestamp(prop.updated_at)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'required',
        header: 'Required',
        size: 60,
        enableEditing: true,
        muiTableBodyCellProps: ({ cell }) => ({
          sx: {
            backgroundColor: cell.getValue() === 'true' ? '#f8d7da' : 'transparent',
            textAlign: 'center',
            paddingTop: '0.5rem',
          },
        }),
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'checkbox',
          onChange: (event) => handleCellEdit(row, cell.column, event.target.checked),
        }),
        Cell: ({ cell, row }) => {
          const prop = vehicleProperty(`${row.original.row_name}_Required`);
          return (
            <Tooltip
              title={
                cell.getValue() === 'true' && prop.user_name && prop.updated_at
                  ? `${prop.user_name} at ${formatTimestamp(prop.updated_at)}`
                  : ''
              }
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={cell.getValue() === 'true'}
                  onChange={(e) => handleCellEdit(row, cell.column, e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                {cell.getValue() === 'true' && prop.user_name && prop.updated_at && (
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#555' }}>
                    {prop.user_name} <br /> {formatTimestamp(prop.updated_at)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'approved',
        header: 'Approved',
        size: 60,
        enableEditing: true,
        muiTableBodyCellProps: ({ cell }) => ({
          sx: {
            backgroundColor: cell.getValue() === 'true' ? '#d4edda' : 'transparent',
            textAlign: 'center',
            paddingTop: '0.5rem',
          },
        }),
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'checkbox',
          onChange: (event) => handleCellEdit(row, cell.column, event.target.checked),
        }),
        Cell: ({ cell, row }) => {
          const prop = vehicleProperty(`${row.original.row_name}_Approved`);
          return (
            <Tooltip
              title={
                cell.getValue() === 'true' && prop.user_name && prop.updated_at
                  ? `${prop.user_name} at ${formatTimestamp(prop.updated_at)}`
                  : ''
              }
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={cell.getValue() === 'true'}
                  onChange={(e) => handleCellEdit(row, cell.column, e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                {cell.getValue() === 'true' && prop.user_name && prop.updated_at && (
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#555' }}>
                    {prop.user_name} <br /> {formatTimestamp(prop.updated_at)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'time',
        header: 'Time',
        size: 45,
        enableEditing: true,
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'text',
          onBlur: (event) => handleCellEdit(row, cell.column, event.target.value),
        }),
      },
      {
        accessorKey: 'labor',
        header: 'Labor',
        size: 85,
        enableEditing: true,
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'text',
          onBlur: (event) => handleCellEdit(row, cell.column, event.target.value),
        }),
      },
      {
        accessorKey: 'laborTotal',
        header: 'Labor Total',
        size: 85,
        enableEditing: false,
        muiTableBodyCellProps: {
          sx: { textAlign: 'center' },
        },
        Cell: ({ row }) => (
          <div>${rowTotals[row.original.row_name]?.laborTotal || '0.00'}</div>
        ),
      },
      {
        accessorKey: 'quantity',
        header: 'Quantity',
        size: 45,
        enableEditing: true,
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'text',
          onBlur: (event) => handleCellEdit(row, cell.column, event.target.value),
        }),
      },
      {
        accessorKey: 'parts',
        header: 'Parts',
        size: 85,
        enableEditing: true,
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'text',
          onBlur: (event) => handleCellEdit(row, cell.column, event.target.value),
        }),
      },
      {
        accessorKey: 'total',
        header: 'Total',
        size: 45,
        enableEditing: false,
        muiTableBodyCellProps: {
          sx: { textAlign: 'center' },
        },
        Cell: ({ row }) => (
          <div>${rowTotals[row.original.row_name]?.total || '0.00'}</div>
        ),
      },
      {
        accessorKey: 'completed',
        header: 'Completed',
        size: 60,
        enableEditing: true,
        muiTableBodyCellProps: ({ cell }) => ({
          sx: {
            backgroundColor: cell.getValue() === 'true' ? '#d4edda' : 'transparent',
            textAlign: 'center',
          },
        }),
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'checkbox',
          onChange: (event) => handleCellEdit(row, cell.column, event.target.checked),
        }),
        Cell: ({ cell, row }) => {
          const prop = vehicleProperty(`${row.original.row_name}_Completed`);
          return (
            <Tooltip
              title={
                cell.getValue() === 'true' && prop.user_name && prop.updated_at
                  ? `${prop.user_name} at ${formatTimestamp(prop.updated_at)}`
                  : ''
              }
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={cell.getValue() === 'true'}
                  onChange={(e) => handleCellEdit(row, cell.column, e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                {cell.getValue() === 'true' && prop.user_name && prop.updated_at && (
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#555' }}>
                    {prop.user_name} <br /> {formatTimestamp(prop.updated_at)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'action',
        header: 'Action',
        size: 40,
        enableEditing: false,
        muiTableBodyCellProps: {
          sx: { textAlign: 'center' },
        },
        Cell: ({ row }) =>
          row.original.row_name.match(/^custom_inspection[0-9]+$/) && (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => handleOpenDeleteModal(row.original.row_name, vehicleProperty(row.original.row_name).id)}
              title="Delete Custom Row"
            >
              ×
            </button>
          ),
      },
    ],
    [rowTotals, vehiclePropertiesMap, current_user, active_vehicle]
  );

  const data = useMemo(() => {
    const result = firstRows.map((row) => {
      const timeVal = parseFloat(vehicleProperty(`${row}_Time`).value || '0');
      const laborVal = parseFloat(vehicleProperty(`${row}_Labor`).value || default_labor_rate_value);
      const quantityVal = parseFloat(vehicleProperty(`${row}_Quantity`).value || '0');
      const partsVal = parseFloat(vehicleProperty(`${row}_Parts`).value || '0');
      const laborTotal = timeVal * laborVal;
      const total = laborTotal + (quantityVal * partsVal);
      return {
        row_name: row,
        pass: vehicleProperty(`${row}_Pass`).value,
        recommend: vehicleProperty(`${row}_Recommend`).value,
        required: vehicleProperty(`${row}_Required`).value,
        approved: vehicleProperty(`${row}_Approved`).value,
        notes: vehicleProperty(`${row}_Notes`).value,
        time: vehicleProperty(`${row}_Time`).value,
        labor: parseFloat(vehicleProperty(`${row}_Labor`).value) || default_labor_rate_value,
        laborTotal: isNaN(laborTotal) ? '0.00' : laborTotal.toFixed(2),
        quantity: vehicleProperty(`${row}_Quantity`).value,
        parts: vehicleProperty(`${row}_Parts`).value,
        total: isNaN(total) ? '0.00' : total.toFixed(2),
        completed: vehicleProperty(`${row}_Completed`).value,
        action: row,
      };
    });
    return result;
  }, [firstRows, vehiclePropertiesMap]);

  const onPrint = async () => {
    try {
      const print_page = document.getElementById('print-inspection');
      const noPrintElements = print_page.querySelectorAll('.no-print, .no-print-totals, .MuiTableToolbar-root .MuiToolbar-root');
      noPrintElements.forEach((el) => el.style.display = 'none');
      const dataUrl = await domtoimage.toPng(print_page);
      noPrintElements.forEach((el) => el.style.display = '');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const width = pdf.internal.pageSize.getWidth();
      const height = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const imgWidth = width - margin * 2;
      const imgHeight = (imgWidth * print_page.offsetHeight) / print_page.offsetWidth;
      pdf.addImage(dataUrl, 'PNG', margin, margin, imgWidth, imgHeight);
      pdf.save(`vehicle_inspection_${active_vehicle.stock}.pdf`);
    } catch (error) {
      setError('Failed to generate PDF. Please try again.');
    }
  };

  const globalTheme = useTheme();
  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
        },
      }),
    [globalTheme]
  );

  if (!active_vehicle) {
    return <div>No active vehicle selected.</div>;
  }

  return (
    <>
      <style>
        {`
          @media print {
            .no-print, .no-print-totals, .MuiTableToolbar-root .MuiToolbar-root {
              display: none !important;
            }
            td:last-child, th:last-child {
              display: none !important;
            }
          }
        `}
      </style>
      {error && <div className="alert alert-danger">{error}</div>}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>Delete Custom Row</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteRowName || 'this custom row'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <div className="container-fluid pad-left-2 pad-right-2" id="print-inspection">
        <ThemeProvider theme={tableTheme}>
          <MaterialReactTable
            tableRef={tableRef}
            columns={columns}
            data={data}
            enableEditing
            enablePagination={false}
            editDisplayMode="cell"
            initialState={{
              density: 'compact',
              pagination: { pageIndex: 0, pageSize: 50 },
              columnVisibility: { labor: false },
            }}
            muiTablePaginationProps={{
              sx: { display: 'none' },
            }}
            renderTopToolbarCustomActions={() => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  p: 2,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                }}
              >
                <Stack direction="column" spacing={1}>
                  <Typography variant="h6">
                    {active_vehicle.year} {active_vehicle.make} {active_vehicle.model}
                  </Typography>
                  <Typography variant="caption">
                    <strong>Stock:</strong> {active_vehicle.stock} <strong>VIN:</strong> {active_vehicle.vin}
                  </Typography>
                </Stack>
                <Stack direction="column" spacing={1}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Typography variant="body2">
                      <strong>Approved Total:</strong> ${approvedTotal}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Estimate:</strong> ${grandTotal}
                    </Typography>
                  </Stack>
                </Stack>
                <Box className="no-print">
                  <Button
                    variant="contained"
                    color="success"
                    onClick={onPrint}
                    sx={{ mr: 1 }}
                  >
                    Print
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={addCustomRow}
                  >
                    Add Row
                  </Button>
                </Box>
              </Box>
            )}
            renderBottomToolbarCustomActions={() => (
              <Box sx={{ display: 'flex', justifyContent: 'right', width: '100%', paddingRight: '150px' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Typography variant="body2">
                      <strong>Total Estimate:</strong> ${grandTotal}
                    </Typography>
                  <Typography variant="body2">
                    <strong>Approved Total:</strong> ${approvedTotal}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Completed Total:</strong> ${completedTotal}
                  </Typography>
                </Stack>
              </Box>
            )}
            muiTableContainerProps={{
              sx: { padding: '0 15px' },
            }}
          />
        </ThemeProvider>
      </div>
    </>
  );
};

export default InspectionBuildForm;